import React from 'react'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import { useFormContext } from 'react-hook-form'

import { FormFileDropzone, FormTextField } from 'components/input/index'
import formArray from 'decorators/formArray'
import { withForm } from 'decorators/withForm'
import { useAddNotification } from 'hooks/useAddNotification'
import { type BannerType, validateBannerFile } from 'models/campaign-banners-model'
import { getFileDimensions, getHtml5Dimensions } from 'utils/file'
import { stripFileExtension } from 'utils/strings'
import { campaignBannerUpload } from 'validators/campaign-banners'

const FORM_NAME = 'bannerUpload'

type CampaignBannerUploadProps = {
  change: (...args: any[]) => any
  field: Record<string, any>
  handleSubmit: (...args: any[]) => any
  open: boolean
  title: string
  onClose: (...args: any[]) => any
  onSubmit: (...args: any[]) => any
}

const CampaignBannerUpload = ({
  change,
  field,
  handleSubmit,
  open,
  title,
  onClose,
  onSubmit,
}: CampaignBannerUploadProps) => {
  const { getValues, setValue } = useFormContext()
  const addNotification = useAddNotification()

  const handleClose = () => onClose()

  const handleChange = async (files: any) => {
    const customerId = getValues('customerId')
    const type = getValues('type') as BannerType

    const validFiles = files.filter((file: any) => {
      const error = validateBannerFile(file, type)
      if (error) addNotification(`"${file.name}" ${error}`, 'error')
      return !error
    })

    const banners = await Promise.all(
      validFiles.map(async (file: any) => ({
        title: stripFileExtension(file.name),
        ...((type === 'image' ? await getFileDimensions(file) : getHtml5Dimensions(file)) as any),
        file,
        type,
        customerId,
      })),
    )

    change('banners', banners)
    // Touch each banner title so validation surfaces; setValue's shouldTouch is per-registered-field (setting the array name doesn't touch leaves), so do it per-leaf.
    banners.forEach((banner, idx) =>
      setValue(`banners.${idx}.title`, banner.title, { shouldTouch: true, shouldValidate: true }),
    )
  }

  const handleBannerRemove = (idx: any) => () => field.fields.remove(idx)

  const submit = (values: any) => onClose(() => onSubmit(values.banners))

  return (
    <Dialog maxWidth="xl" onClose={handleClose} open={open}>
      <form onSubmit={handleSubmit(submit)}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {field.fields.map((_memberName: string, idx: number) => (
            <Grid container key={idx} spacing={2} style={{ width: 1000 }}>
              <Grid size={{ sm: 7 }}>
                <FormTextField label="Title" name={`banners[${idx}].title`} />
              </Grid>
              <Grid size={{ sm: 2 }}>
                <FormTextField label="Width" name={`banners[${idx}].width`} type="number" />
              </Grid>
              <Grid size={{ sm: 2 }}>
                <FormTextField label="Height" name={`banners[${idx}].height`} type="number" />
              </Grid>
              <Grid size={{ sm: 1 }}>
                <Button onClick={handleBannerRemove(idx)}>remove</Button>
              </Grid>
              <input name={`banners[${idx}].file`} type="hidden" />
            </Grid>
          ))}
          <Grid container spacing={2} style={{ width: 1000 }}>
            <Grid size={{ sm: 12 }}>
              <FormFileDropzone multiple name="file" onChange={handleChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button color="primary" type="submit">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default withForm({
  form: FORM_NAME,
  validate: campaignBannerUpload,
})(formArray(CampaignBannerUpload))
